import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

const CommandPalette = () => {
    const history = useHistory();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [statusMessage, setStatusMessage] = useState('');

    const closePalette = () => {
        setIsOpen(false);
        setQuery('');
        setSelectedIndex(0);
    };

    const commands = useMemo(() => ([
        {
            id: 'go-home',
            label: 'Go to Home',
            hint: 'Route: /',
            keywords: ['home', 'landing', 'main'],
            action: () => history.push('/'),
        },
        {
            id: 'go-about',
            label: 'Go to About',
            hint: 'Route: /about',
            keywords: ['about', 'bio', 'background'],
            action: () => history.push('/about'),
        },
        {
            id: 'go-projects',
            label: 'Go to Projects',
            hint: 'Route: /projects',
            keywords: ['projects', 'work', 'portfolio'],
            action: () => history.push('/projects'),
        },
        {
            id: 'go-contact',
            label: 'Go to Contact',
            hint: 'Route: /contact',
            keywords: ['contact', 'email', 'reach'],
            action: () => history.push('/contact'),
        },
        {
            id: 'open-github',
            label: 'Open GitHub',
            hint: 'External link',
            keywords: ['github', 'code', 'repository'],
            action: () => window.open('https://github.com/jrmronquillo', '_blank', 'noopener,noreferrer'),
        },
        {
            id: 'open-linkedin',
            label: 'Open LinkedIn',
            hint: 'External link',
            keywords: ['linkedin', 'network', 'profile'],
            action: () => window.open('https://www.linkedin.com/in/jerome-ronquillo-mse-4063bb76/', '_blank', 'noopener,noreferrer'),
        },
        {
            id: 'copy-url',
            label: 'Copy Current Page URL',
            hint: 'Clipboard',
            keywords: ['copy', 'url', 'share', 'link'],
            action: async () => {
                try {
                    await navigator.clipboard.writeText(window.location.href);
                    setStatusMessage('Page URL copied to clipboard.');
                } catch (error) {
                    setStatusMessage('Unable to copy URL in this browser.');
                }
            },
        },
    ]), [history]);

    const filteredCommands = useMemo(() => {
        const normalized = query.trim().toLowerCase();

        if (!normalized) {
            return commands;
        }

        return commands.filter((command) => {
            const searchable = `${command.label} ${command.hint} ${command.keywords.join(' ')}`.toLowerCase();
            return searchable.includes(normalized);
        });
    }, [commands, query]);

    useEffect(() => {
        setSelectedIndex(0);
    }, [query]);

    useEffect(() => {
        if (!statusMessage) {
            return undefined;
        }

        const timer = window.setTimeout(() => {
            setStatusMessage('');
        }, 2200);

        return () => window.clearTimeout(timer);
    }, [statusMessage]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            const target = event.target;
            const isTypingField =
                target instanceof HTMLElement &&
                (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable);

            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                setIsOpen((current) => !current);
                return;
            }

            if (!isOpen) {
                return;
            }

            if (event.key === 'Escape') {
                event.preventDefault();
                closePalette();
                return;
            }

            if (isTypingField && event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') {
                return;
            }

            if (event.key === 'ArrowDown') {
                event.preventDefault();
                setSelectedIndex((current) => {
                    if (!filteredCommands.length) {
                        return 0;
                    }
                    return (current + 1) % filteredCommands.length;
                });
                return;
            }

            if (event.key === 'ArrowUp') {
                event.preventDefault();
                setSelectedIndex((current) => {
                    if (!filteredCommands.length) {
                        return 0;
                    }
                    return current === 0 ? filteredCommands.length - 1 : current - 1;
                });
                return;
            }

            if (event.key === 'Enter' && filteredCommands[selectedIndex]) {
                event.preventDefault();
                filteredCommands[selectedIndex].action();
                closePalette();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [filteredCommands, isOpen, selectedIndex]);

    const runCommand = (command) => {
        command.action();
        closePalette();
    };

    if (!isOpen) {
        return (
            <button
                type="button"
                className="command-palette-shortcut"
                onClick={() => setIsOpen(true)}
                aria-label="Open command palette"
            >
                <span>Command Palette</span>
                <kbd>Cmd/Ctrl + K</kbd>
            </button>
        );
    }

    return (
        <>
            <div className="command-palette-overlay" onClick={closePalette} />
            <section className="command-palette" role="dialog" aria-modal="true" aria-label="Command palette">
                <input
                    type="text"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Type a command..."
                    className="command-palette-input"
                    autoFocus
                />

                <ul className="command-palette-list" role="listbox" aria-label="Available commands">
                    {filteredCommands.length ? (
                        filteredCommands.map((command, index) => (
                            <li key={command.id}>
                                <button
                                    type="button"
                                    className={`command-palette-item ${index === selectedIndex ? 'is-selected' : ''}`}
                                    onClick={() => runCommand(command)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                >
                                    <span>{command.label}</span>
                                    <small>{command.hint}</small>
                                </button>
                            </li>
                        ))
                    ) : (
                        <li className="command-palette-empty">No command found.</li>
                    )}
                </ul>
            </section>
            {statusMessage ? (
                <div className="command-palette-status" role="status" aria-live="polite">
                    {statusMessage}
                </div>
            ) : null}
        </>
    );
};

export default CommandPalette;